import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Badge } from '@vaiu/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@vaiu/ui/dialog';
import {
  ArrowRightLeft,
  User,
  Clock,
  DollarSign,
  CheckCircle,
  AlertCircle,
  MessageSquare
} from 'lucide-react';

interface ExchangeListing {
  id: string;
  type: 'offer' | 'request';
  userId: string;
  username: string;
  amount: number;
  currency: string;
  wantedAmount?: number;
  wantedCurrency?: string;
  description: string;
  timeLimit: string;
  acceptPartial: boolean;
  onlyFullAmount: boolean;
  createdAt: string;
  expiresAt: string;
  status: 'active' | 'matched' | 'expired' | 'cancelled';
  matches?: number;
}

interface ExchangeMatchProps {
  listing: ExchangeListing;
  onMatchCreated: (match: any) => void;
}

const ExchangeMatch: React.FC<ExchangeMatchProps> = ({ listing, onMatchCreated }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isMatching, setIsMatching] = useState(false);
  const [matchAmount, setMatchAmount] = useState(listing.acceptPartial ? '0' : listing.amount.toString());
  const [offerAmount, setOfferAmount] = useState('');
  const [message, setMessage] = useState('');
  const [showMatchDialog, setShowMatchDialog] = useState(false);

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return currency + ' ';
    }
  };

  const handleMatch = async () => {
    if (!user) return;
    
    setIsMatching(true);
    try {
      // Calculate the exchange based on the listing type
      let myAmount: number;
      let myCurrency: string;
      let theirAmount: number;
      let theirCurrency: string;
      
      if (listing.type === 'offer') {
        // They're offering X currency, I'm requesting Y currency
        theirAmount = parseFloat(matchAmount);
        theirCurrency = listing.currency;
        myAmount = parseFloat(offerAmount);
        myCurrency = listing.wantedCurrency || listing.currency;
      } else {
        // They're requesting X currency, I'm offering Y currency
        myAmount = parseFloat(matchAmount);
        myCurrency = listing.currency;
        theirAmount = parseFloat(offerAmount);
        theirCurrency = listing.wantedCurrency || listing.currency;
      }
      
      // Validate amounts
      if (myAmount <= 0 || theirAmount <= 0) {
        toast({
          title: 'Invalid Amount',
          description: 'Please enter valid amounts for both sides',
          variant: 'destructive'
        });
        return;
      }
      
      // Check if user has sufficient balance
      // This would normally check against user's actual balance
      const mockBalance = 1000; // Mock balance for demonstration
      if (myAmount > mockBalance) {
        toast({
          title: 'Insufficient Balance',
          description: `You need ${getCurrencySymbol(myCurrency)}${myAmount} but only have ${getCurrencySymbol(myCurrency)}${mockBalance}`,
          variant: 'destructive'
        });
        return;
      }
      
      // Create the match
      const match = {
        id: Date.now().toString(),
        listingId: listing.id,
        matcherId: user.id,
        matcherUsername: user.username,
        myAmount,
        myCurrency,
        theirAmount,
        theirCurrency,
        message,
        status: 'pending',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours
      };
      
      // In a real app, this would POST to /api/exchange/matches
      console.log('Creating match:', match);
      
      onMatchCreated(match);
      setShowMatchDialog(false);
      
      toast({
        title: 'Match Created',
        description: `Your match request has been sent to @${listing.username}`,
      });
      
    } catch (error) {
      console.error('Match creation failed:', error);
      toast({
        title: 'Match Failed',
        description: 'Failed to create match. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsMatching(false);
    }
  };

  const handleContact = () => {
    // In a real app, this would open a chat/messaging interface
    toast({
      title: 'Contact User',
      description: 'Opening chat with @' + listing.username,
    });
  };

  // Don't show match option for user's own listings
  if (listing.userId === user?.id) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-2">
      <Dialog open={showMatchDialog} onOpenChange={setShowMatchDialog}>
        <DialogTrigger asChild>
          <Button variant="default" size="sm">
            Match
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Match with @{listing.username}</DialogTitle>
            <DialogDescription>
              Create a match for this {listing.type === 'offer' ? 'offer' : 'request'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Listing Summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">
                  {listing.type === 'offer' ? 'Their Offer' : 'Their Request'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold">
                      {getCurrencySymbol(listing.currency)}{listing.amount}
                    </span>
                    <span className="text-gray-600">{listing.currency}</span>
                  </div>
                  {listing.wantedAmount && listing.wantedCurrency && (
                    <>
                      <ArrowRightLeft className="w-4 h-4 text-gray-400" />
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold">
                          {getCurrencySymbol(listing.wantedCurrency)}{listing.wantedAmount}
                        </span>
                        <span className="text-gray-600">{listing.wantedCurrency}</span>
                      </div>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-2">{listing.description}</p>
              </CardContent>
            </Card>
            
            {/* Match Form */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="matchAmount">
                  {listing.type === 'offer' ? 'Amount to Take' : 'Amount to Give'}
                </Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="matchAmount"
                    type="number"
                    placeholder="0"
                    value={matchAmount}
                    onChange={(e) => setMatchAmount(e.target.value)}
                    className="flex-1"
                  />
                  <span className="text-gray-600">{listing.currency}</span>
                </div>
                {listing.acceptPartial && (
                  <p className="text-xs text-gray-500 mt-1">
                    Partial amounts accepted (max: {listing.amount})
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="offerAmount">
                  {listing.type === 'offer' ? 'Amount to Give' : 'Amount to Take'}
                </Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="offerAmount"
                    type="number"
                    placeholder="0"
                    value={offerAmount}
                    onChange={(e) => setOfferAmount(e.target.value)}
                    className="flex-1"
                  />
                  <span className="text-gray-600">{listing.wantedCurrency || listing.currency}</span>
                </div>
              </div>
              
              <div>
                <Label htmlFor="message">Message (optional)</Label>
                <Input
                  id="message"
                  placeholder="Add a message to your match..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
            </div>
            
            {/* Exchange Summary */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <h4 className="font-semibold mb-2">Exchange Summary</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">You give</p>
                    <p className="font-bold">
                      {getCurrencySymbol(listing.type === 'offer' ? (listing.wantedCurrency || listing.currency) : listing.currency)}
                      {listing.type === 'offer' ? offerAmount : matchAmount}
                    </p>
                  </div>
                  <ArrowRightLeft className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">You get</p>
                    <p className="font-bold">
                      {getCurrencySymbol(listing.type === 'offer' ? listing.currency : (listing.wantedCurrency || listing.currency))}
                      {listing.type === 'offer' ? matchAmount : offerAmount}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowMatchDialog(false)}
                disabled={isMatching}
              >
                Cancel
              </Button>
              <Button onClick={handleMatch} disabled={isMatching}>
                {isMatching ? 'Creating Match...' : 'Create Match'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Button variant="outline" size="sm" onClick={handleContact}>
        <MessageSquare className="w-4 h-4 mr-1" />
        Contact
      </Button>
    </div>
  );
};

export default ExchangeMatch; 